// src/components/trucks/TruckManagement.js
import React, { useState } from 'react';
import { useApp } from '../../contexts/AppContext';
import { Card, Table, Badge, Button, Modal, FormField, Input, Select, SearchInput, ConfirmDialog, FuelBar, ResponsiveGrid } from '../layout/UI';
import { useResponsive } from '../../hooks/useResponsive';
import { TRUCK_STATUSES } from '../../data/store';

const TRUCK_TYPES = ['Light Duty', 'Medium Duty', 'Heavy Duty', 'Trailer', 'Van', 'Pickup'];

export default function TruckManagement() {
  const { trucks, addTruck, updateTruck, deleteTruck, gpsDevices, clients, currentUser } = useApp();
  const { isMobile } = useResponsive();
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editTruck, setEditTruck] = useState(null);
  const [viewTruck, setViewTruck] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [form, setForm] = useState({ plateNumber: '', model: '', year: '', type: TRUCK_TYPES[0], capacity: '', driver: '', driverPhone: '', dispatcher: '', clientId: '', gpsDeviceId: '' });

  const isClient = currentUser?.role === 'client';
  const myTrucks = isClient ? trucks.filter(t => t.clientId === currentUser.clientId) : trucks;

  const filtered = myTrucks.filter(t =>
    t.plateNumber?.toLowerCase().includes(search.toLowerCase()) ||
    t.model?.toLowerCase().includes(search.toLowerCase()) ||
    t.driver?.toLowerCase().includes(search.toLowerCase())
  );

  const availableGPS = gpsDevices.filter(g => g.status === 'available' || (editTruck && g.assignedTruck === editTruck.id));

  const openAdd = () => {
    setEditTruck(null);
    setForm({ plateNumber: '', model: '', year: new Date().getFullYear().toString(), type: TRUCK_TYPES[0], capacity: '', driver: '', driverPhone: '', dispatcher: '', clientId: '', gpsDeviceId: '' });
    setModalOpen(true);
  };

  const openEdit = (truck) => {
    setEditTruck(truck);
    setForm({ plateNumber: truck.plateNumber, model: truck.model, year: truck.year, type: truck.type, capacity: truck.capacity, driver: truck.driver, driverPhone: truck.driverPhone || '', dispatcher: truck.dispatcher || '', clientId: truck.clientId || '', gpsDeviceId: truck.gpsDeviceId || '' });
    setModalOpen(true);
  };

  const handleSave = () => {
    if (!form.plateNumber || !form.model) return;
    if (editTruck) {
      updateTruck(editTruck.id, form);
    } else {
      addTruck(form);
    }
    setModalOpen(false);
  };

  const columns = [
    {
      key: 'plateNumber', label: 'Truck',
      render: (val, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 10, height: 36, borderRadius: 3, background: row.color || '#3b82f6', flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--textPrimary)', fontFamily: 'Syne, sans-serif' }}>{val}</div>
            <div style={{ fontSize: 11, color: 'var(--textFaint)' }}>{row.model} {row.year} · {row.type}</div>
          </div>
        </div>
      )
    },
    { key: 'driver', label: 'Driver', render: v => <span style={{ fontSize: 12, color: 'var(--textSecondary)' }}>{v || 'Unassigned'}</span> },
    {
      key: 'gpsDeviceId', label: 'GPS',
      render: v => {
        const gps = gpsDevices.find(g => g.id === v);
        return gps ? <Badge color="#8b5cf6">{gps.deviceId}</Badge> : <Badge color="#4a5a7a">No GPS</Badge>;
      }
    },
    {
      key: 'clientId', label: 'Client',
      render: v => {
        const client = clients.find(c => c.id === v);
        return client ? <span style={{ fontSize: 12, color: 'var(--textSecondary)' }}>{client.name}</span> : <span style={{ fontSize: 12, color: 'var(--textFaint)' }}>—</span>;
      }
    },
    {
      key: 'fuelLevel', label: 'Fuel',
      render: v => <div style={{ minWidth: 100 }}><FuelBar level={v || 0} /></div>
    },
    {
      key: 'status', label: 'Status',
      render: v => {
        const s = TRUCK_STATUSES[v] || TRUCK_STATUSES.offline;
        return <Badge color={s.color} bg={s.bg}>{s.label}</Badge>;
      }
    },
    {
      key: 'location', label: 'Location',
      render: v => <span style={{ fontSize: 11, color: 'var(--textMuted)', maxWidth: 150, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v?.address || '—'}</span>
    },
    ...(!isClient ? [{
      key: 'id', label: 'Actions',
      render: (id, row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); setViewTruck(row); }}>View</Button>
          <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); openEdit(row); }}>Edit</Button>
          <Button size="sm" variant="danger" onClick={e => { e.stopPropagation(); setConfirmDelete(row); }}>Del</Button>
        </div>
      )
    }] : [{
      key: 'id', label: '',
      render: (id, row) => <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); setViewTruck(row); }}>View</Button>
    }]),
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 12, alignItems: isMobile ? 'stretch' : 'center', marginBottom: 20 }}>
          <div style={{ flex: 1 }}>
            <SearchInput value={search} onChange={setSearch} placeholder="Search trucks..." />
          </div>
          {!isClient && <Button onClick={openAdd} icon="＋">Add Truck</Button>}
        </div>

        <ResponsiveGrid columns={Object.keys(TRUCK_STATUSES).length} tabletColumns={2} mobileColumns={2} gap={10} style={{ marginBottom: 20 }}>
          {Object.entries(TRUCK_STATUSES).map(([key, status]) => {
            const count = filtered.filter(t => t.status === key).length;
            return (
              <div key={key} style={{ background: 'var(--bgInput)', border: `1px solid ${status.color}22`, borderRadius: 10, padding: '12px 16px' }}>
                <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 26, color: status.color }}>{count}</div>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginTop: 2 }}>{status.label}</div>
              </div>
            );
          })}
        </ResponsiveGrid>

        <Table columns={columns} data={filtered} onRowClick={isClient ? setViewTruck : undefined} emptyMessage="No trucks found" />
      </Card>

      {/* Add/Edit Truck Modal */}
      {!isClient && (
        <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editTruck ? 'Edit Truck' : 'Add New Truck'} width={600}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
            <FormField label="Plate Number" required>
              <Input value={form.plateNumber} onChange={v => setForm(f => ({ ...f, plateNumber: v }))} placeholder="ABC 1234" required />
            </FormField>
            <FormField label="Model" required>
              <Input value={form.model} onChange={v => setForm(f => ({ ...f, model: v }))} placeholder="e.g. Isuzu Elf" required />
            </FormField>
            <FormField label="Year">
              <Input value={form.year} onChange={v => setForm(f => ({ ...f, year: v }))} placeholder="2024" />
            </FormField>
            <FormField label="Type">
              <Select value={form.type} onChange={v => setForm(f => ({ ...f, type: v }))} options={TRUCK_TYPES.map(t => ({ value: t, label: t }))} />
            </FormField>
            <FormField label="Capacity">
              <Input value={form.capacity} onChange={v => setForm(f => ({ ...f, capacity: v }))} placeholder="e.g. 5 tons" />
            </FormField>
            <FormField label="Assign Client">
              <Select value={form.clientId} onChange={v => setForm(f => ({ ...f, clientId: v }))} placeholder="No client" options={clients.map(c => ({ value: c.id, label: c.name }))} />
            </FormField>
            <FormField label="Driver Name">
              <Input value={form.driver} onChange={v => setForm(f => ({ ...f, driver: v }))} placeholder="Driver full name" />
            </FormField>
            <FormField label="Driver Phone">
              <Input value={form.driverPhone} onChange={v => setForm(f => ({ ...f, driverPhone: v }))} placeholder="+63 9XX XXX XXXX" />
            </FormField>
            <FormField label="Dispatcher">
              <Input value={form.dispatcher} onChange={v => setForm(f => ({ ...f, dispatcher: v }))} placeholder="Dispatcher name" />
            </FormField>
            <FormField label="GPS Device" style={{ gridColumn: 'span 2' }}>
              <Select value={form.gpsDeviceId} onChange={v => setForm(f => ({ ...f, gpsDeviceId: v }))} placeholder="No GPS device" options={availableGPS.map(g => ({ value: g.id, label: `${g.deviceId} (${g.model})` }))} />
            </FormField>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
            <Button variant="ghost" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSave}>{editTruck ? 'Save Changes' : 'Add Truck'}</Button>
          </div>
        </Modal>
      )}

      {/* View Detail Modal */}
      <Modal open={!!viewTruck} onClose={() => setViewTruck(null)} title="Truck Details" width={560}>
        {viewTruck && (() => {
          const gps = gpsDevices.find(g => g.id === viewTruck.gpsDeviceId);
          const client = clients.find(c => c.id === viewTruck.clientId);
          const status = TRUCK_STATUSES[viewTruck.status] || TRUCK_STATUSES.offline;
          return (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 16, background: 'var(--bgInput)', borderRadius: 12, border: '1px solid var(--borderInput)', marginBottom: 20 }}>
                <div style={{ width: 14, height: 56, borderRadius: 4, background: viewTruck.color || '#3b82f6', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 22, color: 'var(--textPrimary)' }}>{viewTruck.plateNumber}</div>
                  <div style={{ fontSize: 13, color: 'var(--textMuted)', marginTop: 2 }}>{viewTruck.model} {viewTruck.year} · {viewTruck.type} · {viewTruck.capacity}</div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <Badge color={status.color} bg={status.bg}>{status.label}</Badge>
                    {gps && <Badge color="#8b5cf6">{gps.deviceId}</Badge>}
                    {client && <Badge color="#10b981">{client.name}</Badge>}
                  </div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10, marginBottom: 16 }}>
                {[
                  { label: 'Driver', value: viewTruck.driver },
                  { label: 'Driver Phone', value: viewTruck.driverPhone },
                  { label: 'Dispatcher', value: viewTruck.dispatcher },
                  { label: 'Mileage', value: viewTruck.mileage },
                  { label: 'Last Service', value: viewTruck.lastService },
                  { label: 'Current Location', value: viewTruck.location?.address },
                  { label: 'Speed', value: viewTruck.location?.speed ? `${viewTruck.location.speed} km/h` : '0 km/h' },
                ].map(item => (
                  <div key={item.label} style={{ padding: '10px 14px', background: 'var(--bgInput)', borderRadius: 8, border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 10, color: 'var(--textFaint)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{item.label}</div>
                    <div style={{ fontSize: 13, color: 'var(--textSecondary)' }}>{item.value || '—'}</div>
                  </div>
                ))}
              </div>
              <div style={{ padding: '12px 14px', background: 'var(--bgInput)', borderRadius: 8, border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 10, color: 'var(--textFaint)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Fuel Level</div>
                <FuelBar level={viewTruck.fuelLevel || 0} />
              </div>
            </div>
          );
        })()}
      </Modal>

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => deleteTruck(confirmDelete.id)}
        title="Delete Truck"
        message={`Delete truck "${confirmDelete?.plateNumber}"? This will also unassign its GPS device.`}
      />
    </div>
  );
}
